import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import { PanelSearch } from './PanelSearch'
import { useMediaSrc } from './useMediaSrc'

/**
 * The Library's phone layout (handoff-11 `CRM Library.dc.html`, mobile): a
 * screen listing the lists or categories, then one list or category with a
 * back button, the Telegram-stories-style switcher, a Select mode and a
 * bottom bar. Shared by Collections and Vault; the pages own the data.
 */

/**
 * A full-size tap target under a phone row or tile. Chrome's touch adjustment
 * snaps a finger near a nested button (the select circle, the avatar) onto that
 * button; a sibling layer that responds to clicks keeps taps on the body for
 * the body. The click bubbles to the row's own handler.
 */
export function TapLayer() {
  return <span className="lib-mhit" aria-hidden="true" onClick={tapThrough} />
}
function tapThrough() {
  /* the row's onClick handles it */
}

/** One list or category in the switcher (carousel and the header stack). */
export interface SwitchItem {
  id: string
  label: string
  on: boolean
  thumb: ThumbSpec
}

export interface ThumbSpec {
  /** OnlyFans image to show; loaded when on screen. */
  url?: string
  /** Shown when there is no image (a name's first letter). */
  initial?: string
  icon?: string
  /** Fans' lists are round, vault categories rounded squares. */
  round?: boolean
  /** The accent fill (All media). */
  tint?: boolean
}

/** A list or category preview that fills its box. */
export function Thumb({ spec, iconSize = 16 }: { spec: ThumbSpec; iconSize?: number }) {
  const { ref, src } = useMediaSrc(spec.url ?? '')
  return (
    <span
      ref={ref as React.RefObject<HTMLSpanElement>}
      className={`lib-thumb ${spec.round ? 'is-round' : ''} ${spec.tint ? 'is-tint' : ''}`}
    >
      {src ? <img src={src} alt="" loading="lazy" /> : spec.icon ? <FeatherIcon name={spec.icon} size={iconSize} /> : spec.initial}
    </span>
  )
}

/** Level 0: the page title, "+", search and the rows (passed as children). */
export function MobileListScreen({
  title,
  addLabel,
  onAdd,
  actions,
  searchLabel,
  query,
  onQuery,
  children,
}: {
  title: string
  addLabel: string
  onAdd?: () => void
  /** More buttons beside "+" (the Vault's Upload). */
  actions?: ReactNode
  searchLabel: string
  query: string
  onQuery: (q: string) => void
  children: ReactNode
}) {
  return (
    <>
      <header className="lib-mtop">
        <h1 className="lib-mtop-title">{title}</h1>
        {actions}
        {onAdd && (
          <button type="button" className="lib-mbtn lib-mbtn--accent" aria-label={addLabel} title={addLabel} onClick={onAdd}>
            <FeatherIcon name="plus" size={22} />
          </button>
        )}
      </header>
      <div className="lib-msearch">
        <PanelSearch label={searchLabel} value={query} onChange={onQuery} />
      </div>
      <nav className="lib-mrows" aria-label={title}>
        {children}
      </nav>
    </>
  )
}

/** Level 1 header: back, the name with the switcher stack, Select and More. */
export function MobileOpenHead({
  title,
  sub,
  onBack,
  switcher,
  onSelect,
  more,
}: {
  title: ReactNode
  sub: string
  onBack: () => void
  switcher?: ReactNode
  onSelect?: () => void
  more?: ReactNode
}) {
  const { t } = useTranslation('library')
  return (
    <header className="lib-mhead">
      <button type="button" className="lib-mbtn" aria-label={t('mobile.back')} onClick={onBack}>
        <FeatherIcon name="chevron-left" size={24} />
      </button>
      <span className="lib-mhead-text">
        <span className="lib-mhead-line">
          {typeof title === 'string' ? <h2 className="lib-mhead-title">{title}</h2> : title}
          {switcher}
        </span>
        <span className="lib-mhead-sub">{sub}</span>
      </span>
      {onSelect && (
        <button type="button" className="lib-mtext-btn" onClick={onSelect}>
          {t('select')}
        </button>
      )}
      {more}
    </header>
  )
}

/** Level 1 header in Select mode: cancel, "N selected", Select all. */
export function MobileSelectHead({ count, onCancel, onSelectAll }: { count: number; onCancel: () => void; onSelectAll: () => void }) {
  const { t } = useTranslation('library')
  return (
    <header className="lib-mhead is-selecting">
      <button type="button" className="lib-mbtn" aria-label={t('mobile.doneSelecting')} onClick={onCancel}>
        <FeatherIcon name="x" size={22} />
      </button>
      <span className="lib-mhead-title lib-mhead-count">{count ? t('selected', { count }) : t('mobile.selectItems')}</span>
      <button type="button" className="lib-mtext-btn" onClick={onSelectAll}>
        {t('mobile.selectAll')}
      </button>
    </header>
  )
}

/** The thumbnail stack after the name: opens and closes the carousel. */
export function SwitchStack({ items, open, onToggle, label }: { items: SwitchItem[]; open: boolean; onToggle: () => void; label: string }) {
  const others = items.filter((i) => !i.on).slice(0, 3)
  if (!others.length) return null
  return (
    <button
      type="button"
      className={`lib-mstack ${open ? 'is-open' : ''}`}
      aria-label={label}
      aria-expanded={open}
      onClick={onToggle}
    >
      <span className="lib-mstack-thumbs" aria-hidden="true">
        {others.map((i) => (
          <span key={i.id} className="lib-mstack-i">
            <Thumb spec={i.thumb} iconSize={11} />
          </span>
        ))}
      </span>
      <FeatherIcon name="chevron-down" size={14} className="lib-mstack-chev" />
    </button>
  )
}

/** The other lists or categories as a row of stories (design: Carousel). */
export function SwitchCarousel({ items, open, onPick, label }: { items: SwitchItem[]; open: boolean; onPick: (id: string) => void; label: string }) {
  return (
    <div className={`lib-car ${open ? 'is-open' : ''}`} aria-hidden={!open}>
      <div className="lib-car-row" role="group" aria-label={label}>
        {items.map((i) => (
          <button
            key={i.id}
            type="button"
            className={`lib-car-item ${i.on ? 'is-on' : ''} ${i.thumb.round ? 'is-round' : ''}`}
            aria-current={i.on ? 'true' : undefined}
            tabIndex={open ? 0 : -1}
            onClick={() => onPick(i.id)}
          >
            <span className="lib-car-ring">
              <Thumb spec={i.thumb} iconSize={20} />
            </span>
            <span className="lib-car-label">{i.label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

export interface MobileAction {
  icon: string
  label: string
  onPress: () => void
  disabled?: boolean
  title?: string
}

/** Select mode's bottom bar (replaces the tab bar). */
export function MobileActionBar({ actions, label }: { actions: MobileAction[]; label: string }) {
  return (
    <nav className="lib-mbar" aria-label={label}>
      {actions.map((a) => (
        <button key={a.label} type="button" className="lib-mbar-btn" disabled={a.disabled} title={a.title} onClick={a.onPress}>
          <FeatherIcon name={a.icon} size={21} />
          {a.label}
        </button>
      ))}
    </nav>
  )
}

/** A bulk job running: its progress where the bottom bar was. */
export function MobileProgressBar({ text, done, total, onCancel }: { text: string; done: number; total: number; onCancel: () => void }) {
  const { t } = useTranslation('library')
  return (
    <div className="lib-mbar lib-mbar--progress" role="group" aria-label={t('collections.progressLabel')}>
      <span className="lib-mbar-text">{text}</span>
      <span className="lib-progress" aria-hidden="true">
        <span style={{ width: `${(done / Math.max(1, total)) * 100}%` }} />
      </span>
      <button type="button" className="lib-mtext-btn" onClick={onCancel}>
        {t('common.cancel')}
      </button>
    </div>
  )
}

/** The group label row ("Lists 15") on the level 0 screen. */
export function MobileGroup({ label, count }: { label: string; count: number }) {
  return (
    <div className="lib-mgroup">
      <span>{label}</span>
      <span>{count}</span>
    </div>
  )
}
